import type { QueryClient } from '@tanstack/react-query'

import type { Entity } from '@models/Entity'
import { formatDate } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { Step } from '../../../theme/components/Stepper'
import { queryKey as queryKeyBijlageAssigned } from '../../bijlagen/hooks/useAssignedBijlagenForEntity'
import { queryKey as queryKeySubprocessen } from '../hooks/useSubprocessenForEntity'
import type { Subproces } from '../types/Subproces'
import { SubprocesStatus } from '../types/Subproces'

export {
  isAssigneeOnSubproces,
  getStatusTransitions,
  invalidateSubprocessenForEntityAfterTimeout,
  getPausedLogEntry,
  getCancelReason,
  getPausedInformation,
  getPausedCaptionForStatus,
  getStatusLabelForType,
  getStatusBeforePaused,
  isLastSubprocesOfType,
  isStepDisabled,
  canEditSubproces,
  generateStepStatuses,
  createSubprocesUpdate,
}

export type { StepConfig }

/**
 * Beschrijft één stap in een subproces-stepper: de status die de stap
 * representeert en of de stap optioneel/overslaanbaar is.
 *
 * De type-parameter `S` legt vast welke statussen dit subproces daadwerkelijk
 * kent, zodat het resultaat van generateStepStatuses alleen die statussen bevat.
 */
type StepConfig<S extends Subproces['status'] = Subproces['status']> = {
  status: S
  optional?: boolean
}

/**
 * Bepaalt of een gebruiker een subproces mag bewerken.
 * - mode 'and': alleen als de gebruiker combi is én toegewezen op het subproces
 * - mode 'or': als de gebruiker combi is óf toegewezen op het subproces
 */
const canEditSubproces = ({
  user,
  subproces,
  mode,
}: {
  user: AuthenticatedUser
  subproces: Subproces
  mode: 'and' | 'or'
}) => {
  const isAssignee = isAssigneeOnSubproces({ user, subproces })

  return mode === 'and'
    ? !!user.combi && isAssignee
    : !!user.combi || isAssignee
}

/**
 * Genereert een lijst van 'stap-statussen' per meegegeven stap, op basis van de huidige status en de log van het subproces.
 *
 * - stappen vóór de huidige status  -> 'completed'
 * - de huidige status               -> 'required'
 * - stappen ná de huidige status    -> 'optional' (indien optioneel) of 'required'
 * - alles bij status 'done'         -> 'completed', behalve een optionele stap
 *                                      die nooit is doorlopen -> 'skipped'
 * - de status van vóór pauze         -> 'paused'
 */
const generateStepStatuses = <S extends Subproces['status']>({
  subproces,
  steps,
}: {
  subproces: Subproces
  steps: readonly StepConfig<S>[]
}) => {
  const { status, log = [] } = subproces

  const statusBeforePaused = getStatusBeforePaused({ subproces })
  const effectiveStatus = statusBeforePaused ?? status

  // Bevat uitsluitend de statussen die dit subproces kent (uit `steps`).
  const statuses = {} as Record<S, Step['status']>

  if (status === 'done') {
    steps.forEach((step) => {
      statuses[step.status] = step.optional
        ? log.some((entry) => entry.status === step.status)
          ? 'completed'
          : 'skipped'
        : 'completed'
    })
  } else {
    const currentIndex = steps.findIndex((s) => s.status === effectiveStatus)

    steps.forEach((step, index) => {
      if (index < currentIndex) {
        statuses[step.status] = 'completed'
      } else if (index === currentIndex) {
        statuses[step.status] = 'required'
      } else {
        statuses[step.status] = step.optional ? 'optional' : 'required'
      }
    })
  }

  if (statusBeforePaused) {
    statuses[statusBeforePaused as S] = 'paused'
  }

  return { statuses }
}

const createSubprocesUpdate = ({
  subproces,
  status,
  dueDate,
  assignedTo,
}: {
  subproces: Subproces
  status: Subproces['status']
  dueDate?: Date | null
  assignedTo?: string
}) => {
  const update: {
    entity: Entity
    subprocesInfo: Partial<Subproces>
  } = {
    entity: subproces.entity,
    subprocesInfo: {
      _id: subproces._id,
      status,
    },
  }

  if (assignedTo) {
    update.subprocesInfo.assignedTo = assignedTo
  }

  if (dueDate) {
    update.subprocesInfo.dueDate = dueDate.toISOString()
  }

  return update
}

const isAssigneeOnSubproces = ({
  subproces,
  user,
}: {
  subproces: Subproces
  user: AuthenticatedUser
}) => subproces.assignedTo === user.organisatie

const isStepDisabled = ({
  subproces,
  expectedStatus,
  isPending,
}: {
  subproces: Subproces
  expectedStatus: Subproces['status']
  isPending?: boolean
}) =>
  isPending ||
  subproces.status === 'paused' ||
  subproces.status !== expectedStatus

const getStatusTransitions = ({ type, status }: Subproces) => {
  switch (type) {
    case 'schouw':
      return {
        planning: ['created', 'paused', 'rejected'].includes(status),
        review: status === 'planned',
        cancel: ['created', 'planned', 'rejected', 'paused'].includes(status),
        herplan: ['planned'].includes(status),
        pause: ['created', 'planned', 'paused', 'rejected'].includes(status),
      }

    default:
      return {}
  }
}

/** Returns log entry if subproces is currently paused. */
const getPausedLogEntry = ({ subproces }: { subproces: Subproces }) => {
  const { status, log = [] } = subproces

  if (status !== 'paused') return undefined

  return log.findLast(({ status }) => status === 'paused')
}

/**
 * Returns the reason a subproces was cancelled, if it was cancelled with one.
 * Niet elk subproces vraagt een reden bij annuleren (bijv. bodem/netcheck), dus
 * dit kan undefined zijn ook als het subproces geannuleerd is.
 */
const getCancelReason = ({ subproces }: { subproces: Subproces }) => {
  const { status, log = [] } = subproces

  if (status !== 'cancelled') return undefined

  return log.findLast(({ status }) => status === 'cancelled')?.text
}

/** Returns previous subproces status if subproces is currently paused. */
const getStatusBeforePaused = ({ subproces }: { subproces: Subproces }) => {
  const { status, log = [] } = subproces

  if (status !== 'paused') return undefined

  const pausedLogEntry = getPausedLogEntry({ subproces })

  if (pausedLogEntry) {
    const index = log.indexOf(pausedLogEntry)
    const beforePausedLogEntry = log[index - 1]

    return beforePausedLogEntry.status
  }
}

/**
 * Returns an informative string that tells the user who paused the proces, and
 * why/when, if subproces is currently paused.
 */
const getPausedInformation = ({ subproces }: { subproces: Subproces }) => {
  const pausedLogEntry = getPausedLogEntry({ subproces })

  if (!pausedLogEntry) return undefined

  return `Proces gepauzeerd door ${pausedLogEntry.organisatie} op ${formatDate(
    pausedLogEntry.date,
  )} met reden: ${pausedLogEntry.text}` as const
}

/**
 * Caption voor stepper met paused informatie.
 * wordt per stap bepaald of deze wel of niet weergegevn wordt, obv de status voor het pauzeren
 */
const getPausedCaptionForStatus = ({
  subproces,
  status,
}: {
  subproces: Subproces
  status: Subproces['status']
}) =>
  getStatusBeforePaused({ subproces }) === status
    ? getPausedInformation({ subproces })
    : undefined

const DEFAULT_DELAY = 2000

const invalidateSubprocessenForEntityAfterTimeout = async (
  queryClient: QueryClient,
  entity: Entity,
) => {
  await new Promise<void>((resolve) => {
    queryClient.setQueryData([queryKeySubprocessen, entity], (oldData: any) => {
      if (!oldData || !Array.isArray(oldData)) return oldData

      return oldData.map((subproces: Subproces) => ({
        ...subproces,
        refreshing: true, // gebruikt voor een visualisatie van een refresh in de UI, zie subproces components
      }))
    })

    /* v8 ignore next -- @preserve */
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: [queryKeySubprocessen, entity],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeyBijlageAssigned, entity],
      })
      resolve()
    }, DEFAULT_DELAY)
  })
}

/**
 * Geeft een label terug per status. Heeft altijd een fallback naar het generieke label
 * Het is dus alleen nodig om hier specifieke labels te definiëren als deze afwijken van het generieke label
 *
 * voorbeeld: netcheck status done heeft als label 'Vermogen beschikbaar' ipv 'Afgerond'
 */
const getStatusLabelForType = (
  type: Subproces['type'],
  status: Subproces['status'],
) => {
  switch (type) {
    case 'netcheck': {
      if (status === 'in_progress') return 'Netcheck wordt uitgevoerd'
      if (status === 'criteria_klant_verzamelen')
        return 'Criteria netcheck klant verzamelen'
      if (status === 'done') return 'Vermogen beschikbaar'
      break
    }
    case 'bodem': {
      if (status === 'review') return 'Bodemadvies opstellen'
      if (status === 'done') return 'Opgeleverd'
      break
    }
    case 'schouw': {
      if (status === 'done') return 'Goedgekeurd'
      break
    }
  }

  return SubprocesStatus[status]
}

const isLastSubprocesOfType = ({
  subprocessen,
  subproces,
}: {
  subprocessen: Subproces[]
  subproces: Subproces
}) => {
  const { type, _id } = subproces

  const lastOfType = subprocessen.filter((s) => s.type === type).at(-1)

  if (!lastOfType) return true

  return lastOfType._id === _id
}
